// ────────────────────────────────────────
// BULL 차트 시리즈 — 운용 초기 "빈 차트" 해소 (효신님 2026-06-13 단위 자동 전환 안)
// 단계: 스냅샷 0~1개 = 오늘 인트라데이(5분 역산) → 2개+ = 일 단위 → 30개+ = 최근 30일
// 인트라데이 역산 원리: 보유량 환산계수 k = value_bull / cur_price (= qty × fx, 환율 포함)
//   → 시각별 BULL 가치 = Σ(종목 분봉 close × k) + 현금. 오늘 매매가 있으면 근사(보유량 변동 미반영).
// ────────────────────────────────────────

export type BullPoint = { t: number; v: number };

/**
 * 보유 포지션 × 종목별 오늘 분봉 → BULL 가치 시계열.
 * 종목별 t 그리드가 다르면(장 시간 차이) 합집합 그리드에 forward-fill, 시작 전엔 첫 값 backfill.
 * 캔들 전무·계수 계산 불가 시 null (호출부는 flat 폴백).
 */
export function buildBullIntraday(
  // 서버 포트폴리오 행 — 숫자 필드는 문자열로 올 수 있어 Number()로 정규화한다
  positions: { symbol: string; cur_price?: unknown; value_bull?: unknown }[],
  candlesBySymbol: Record<string, { t: number; c: number }[]>,
  cash: number,
): BullPoint[] | null {
  const series: { ts: number[]; cs: number[]; k: number }[] = [];
  let flatBase = cash; // 현금 + 캔들 누락 종목의 상수 기여
  for (const p of positions || []) {
    const cur = Number(p.cur_price), val = Number(p.value_bull);
    if (!Number.isFinite(cur) || cur <= 0 || !Number.isFinite(val)) return null;
    const cands = candlesBySymbol[p.symbol];
    if (!cands || !cands.length) {
      // 캔들 못 받은 종목을 빼면 그 가치만큼 차트가 급락 오표시 (terra 버그5)
      // → 현재 스냅샷 가치(value_bull)를 상수로 기여시켜 총자산 수준 유지 (평평 근사)
      flatBase += val;
      continue;
    }
    series.push({ ts: cands.map((x) => x.t), cs: cands.map((x) => Number(x.c)), k: val / cur });
  }
  if (!series.length) return null; // 전 종목 캔들 전무 — 역산 불가 (호출부 flat 폴백)

  const grid = [...new Set(series.flatMap((s) => s.ts))].sort((a, b) => a - b);
  return grid.map((t) => {
    let v = flatBase;
    for (const s of series) {
      // t 이하 마지막 캔들 (forward-fill) — 없으면 첫 캔들 (backfill)
      let idx = -1;
      for (let i = 0; i < s.ts.length && s.ts[i] <= t; i++) idx = i;
      const c = s.cs[idx < 0 ? 0 : idx];
      v += c * s.k;
    }
    return { t, v };
  });
}

// 12칸 단위 사다리 (효신님 2026-06-13) — 각 단위 ×12가 커버 기간. 12칸 넘는 순간 다음 단위 승급
const LADDER = [
  { s: 300, label: '5분' }, { s: 900, label: '15분' }, { s: 1800, label: '30분' },
  { s: 3600, label: '1시간' }, { s: 14400, label: '4시간' }, { s: 86400, label: '1일' },
  { s: 604800, label: '7일' }, { s: 2592000, label: '30일' },
];

/**
 * 가입(anchor) 기준 12칸 bucket — 실자산 차트(12개 봉)와 시각 일관.
 * - 단위 = 경과시간이 12칸 안에 들어가는 가장 촘촘한 것 (5분→…→30일 자동 승급)
 * - 12칸이 다 차기 전엔 채워진 만큼만 반환 (왼쪽부터 차오르는 연출)
 * - 360일 초과 시 30일 고정 + 최근 12칸 슬라이드
 * - 각 bucket = 마지막 관측값 · 공백 bucket = 직전 값 forward-fill
 */
export function bucketBullSeries(
  points: BullPoint[],
  anchorSec: number,
  nowSec: number,
): { values: number[]; label: string; filled: number } | null {
  const pts = (points || []).filter((p) => p.t >= anchorSec && Number.isFinite(p.v)).sort((a, b) => a.t - b.t);
  if (!pts.length) return null;
  const elapsed = Math.max(0, nowSec - anchorSec);
  const step = LADDER.find((l) => elapsed <= 12 * l.s) || LADDER[LADDER.length - 1];
  const anchorEff = elapsed > 12 * step.s ? nowSec - 12 * step.s : anchorSec;
  const nBuckets = Math.min(12, Math.floor((nowSec - anchorEff) / step.s) + 1);

  const slots: (number | null)[] = Array(nBuckets).fill(null);
  for (const p of pts) {
    const idx = Math.min(nBuckets - 1, Math.floor((p.t - anchorEff) / step.s));
    if (idx >= 0) slots[idx] = p.v; // 정렬돼 있어 자연히 "마지막 관측값"이 남음
  }
  const values: number[] = [];
  let prev: number | null = null;
  for (const s of slots) {
    const v: number = s ?? prev ?? pts[0].v; // 공백 ffill · 선두 공백은 첫 관측값 (타입 명시 — TS7022 순환 추론 차단)
    values.push(v);
    prev = v;
  }
  return { values, label: step.label, filled: values.length };
}
